import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { invalidatePurchaserConfigTemplate } from "@src/presentation/providers/purchaserConfigTemplateCache";
import {
  Purchaser as PurchaserEntity,
  type Purchaser,
  type PurchaserBrandAssociation,
  type PurchaserConfigTemplate,
  type PurchaserPortalKey,
} from "@src/domain/entities/purchaser";
import type {
  CreateBrandPurchaserAssociationDTO,
  CreatePurchaserDTO,
  CreatePurchaserPortalKeyDTO,
  UpdatePurchaserConfigTemplateDTO,
  UpdatePurchaserDTO,
  UpdatePurchaserPortalKeyDTO,
} from "@src/domain/dto/purchasers";
import {
  PurchaserRepository,
  buildPurchaserBrandsMap,
  seedPurchaserConfigTemplateCache,
} from "@src/infrastructure/repositories/purchaser";
import {
  CreateBrandPurchaserAssociationUseCase,
  CreatePurchaserPortalKeyUseCase,
  CreatePurchaserUseCase,
  GetPurchaserBrandsUseCase,
  GetPurchaserConfigTemplateUseCase,
  GetPurchaserPortalKeysUseCase,
  GetPurchasersUseCase,
  UpdatePurchaserConfigTemplateUseCase,
  UpdatePurchaserPortalKeyUseCase,
  UpdatePurchaserUseCase,
} from "@src/application/useCases/purchaser";

interface PurchasersPageCacheEntry {
  purchasers: Purchaser[];
  total: number;
  totalActiveCount: number;
  page: number;
  limit: number;
}

/** Cache key for a single page: "page-limit". */
function purchasersPageCacheKey(page: number, limit: number): string {
  return `${page}-${limit}`;
}

/**
 * Persistent cache survives provider unmount/remount.
 * This is what prevents loader flicker when revisiting a page.
 */
const persistentPurchasersPageCache = new Map<
  string,
  PurchasersPageCacheEntry
>();

/** Per-purchaser config template; null means fetched with no template yet. */
const persistentPurchaserConfigTemplateCache = new Map<
  number,
  PurchaserConfigTemplate | null
>();

/** Per-purchaser portal keys. An empty array means fetched with no keys. */
const persistentPurchaserPortalKeyCache = new Map<number, PurchaserPortalKey[]>();

const optimisticallyCreatedPurchaserKeys = new Set<string>();

function getPurchaserIdentityKeys(purchaser: Purchaser): string[] {
  return [
    `id:${purchaser.purchaserId}`,
    `key:${purchaser.purchaserKey.trim().toUpperCase()}`,
  ];
}

function hasAnyPurchaserIdentity(
  purchaser: Purchaser,
  identities: ReadonlySet<string>,
): boolean {
  return getPurchaserIdentityKeys(purchaser).some((key) =>
    identities.has(key),
  );
}

function removeOptimisticCreatedPurchasersFromPage(
  items: Purchaser[],
  targetPage: number,
): Purchaser[] {
  const shouldFilter =
    targetPage > 1 && optimisticallyCreatedPurchaserKeys.size > 0;

  if (!shouldFilter) return items;

  return items.filter(
    (item) => !hasAnyPurchaserIdentity(item, optimisticallyCreatedPurchaserKeys),
  );
}

interface PurchasersDataContextValue {
  purchasers: Purchaser[];
  total: number;
  totalActiveCount: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
  purchaserBrands: Record<number, PurchaserBrandAssociation[]>;
  purchaserBrandsLoading: Record<number, boolean>;
  setPage: (page: number) => void;
  refetchPurchasers: () => void;
  fetchPurchaserBrands: (purchaserId: number) => Promise<PurchaserBrandAssociation[]>;
  getPurchaserConfigTemplate: (
    purchaserId: number,
  ) => Promise<PurchaserConfigTemplate | null>;
  updatePurchaserConfigTemplate: (
    params: UpdatePurchaserConfigTemplateDTO,
  ) => Promise<PurchaserConfigTemplate>;
  getPurchaserPortalKeys: (purchaserId: number) => Promise<PurchaserPortalKey[]>;
  getCachedPurchaserPortalKeys: (
    purchaserId: number,
  ) => PurchaserPortalKey[] | undefined;
  createPurchaserPortalKey: (
    params: CreatePurchaserPortalKeyDTO,
  ) => Promise<PurchaserPortalKey>;
  updatePurchaserPortalKey: (
    params: UpdatePurchaserPortalKeyDTO,
  ) => Promise<PurchaserPortalKey>;
  createPurchaser: (params: CreatePurchaserDTO) => Promise<Purchaser>;
  updatePurchaser: (params: UpdatePurchaserDTO) => Promise<Purchaser>;
  createBrandPurchaserAssociation: (
    params: CreateBrandPurchaserAssociationDTO,
  ) => Promise<PurchaserBrandAssociation>;
  applyPurchaserCreate: (purchaser: Purchaser) => void;
  applyPurchaserUpdate: (purchaser: Purchaser) => void;
  applyBrandAssociationUpdate: (
    purchaserId: number,
    association: PurchaserBrandAssociation,
  ) => void;
}

const PurchasersDataContext = createContext<PurchasersDataContextValue | null>(
  null,
);

// eslint-disable-next-line react-refresh/only-export-components
export function usePurchasersData(): PurchasersDataContextValue {
  const context = useContext(PurchasersDataContext);
  if (!context) {
    throw new Error("usePurchasersData must be used within PurchasersDataProvider");
  }
  return context;
}

interface PurchasersDataProviderProps {
  children: ReactNode;
  defaultPageNumber?: number;
  defaultPageSize?: number;
}

export function PurchasersDataProvider({
  children,
  defaultPageNumber = 1,
  defaultPageSize = 15,
}: Readonly<PurchasersDataProviderProps>) {
  const location = useLocation();
  const repository = useMemo(() => new PurchaserRepository(), []);
  const getPurchasersUseCase = useMemo(
    () => new GetPurchasersUseCase(repository),
    [repository],
  );
  const getPurchaserBrandsUseCase = useMemo(
    () => new GetPurchaserBrandsUseCase(repository),
    [repository],
  );
  const createPurchaserUseCase = useMemo(
    () => new CreatePurchaserUseCase(repository),
    [repository],
  );
  const updatePurchaserUseCase = useMemo(
    () => new UpdatePurchaserUseCase(repository),
    [repository],
  );
  const createBrandPurchaserAssociationUseCase = useMemo(
    () => new CreateBrandPurchaserAssociationUseCase(repository),
    [repository],
  );
  const getPurchaserConfigTemplateUseCase = useMemo(
    () => new GetPurchaserConfigTemplateUseCase(repository),
    [repository],
  );
  const updatePurchaserConfigTemplateUseCase = useMemo(
    () => new UpdatePurchaserConfigTemplateUseCase(repository),
    [repository],
  );
  const getPurchaserPortalKeysUseCase = useMemo(
    () => new GetPurchaserPortalKeysUseCase(repository),
    [repository],
  );
  const createPurchaserPortalKeyUseCase = useMemo(
    () => new CreatePurchaserPortalKeyUseCase(repository),
    [repository],
  );
  const updatePurchaserPortalKeyUseCase = useMemo(
    () => new UpdatePurchaserPortalKeyUseCase(repository),
    [repository],
  );

  const shouldLoad = location.pathname === "/purchasers";

  const initialCacheKey = purchasersPageCacheKey(
    defaultPageNumber,
    defaultPageSize,
  );
  const initialCached = persistentPurchasersPageCache.get(initialCacheKey);

  const [purchasers, setPurchasers] = useState<Purchaser[]>(
    () => initialCached?.purchasers ?? [],
  );
  const [total, setTotal] = useState(() => initialCached?.total ?? 0);
  const [totalActiveCount, setTotalActiveCount] = useState(
    () => initialCached?.totalActiveCount ?? 0,
  );
  const [page, setPage] = useState(
    () => initialCached?.page ?? defaultPageNumber,
  );
  const [limit] = useState(() => initialCached?.limit ?? defaultPageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forcePurchasersListRefresh, setForcePurchasersListRefresh] =
    useState(false);
  const [purchaserBrands, setPurchaserBrands] = useState<
    Record<number, PurchaserBrandAssociation[]>
  >(() =>
    initialCached ? buildPurchaserBrandsMap(initialCached.purchasers) : {},
  );
  const [purchaserBrandsLoading, setPurchaserBrandsLoading] = useState<
    Record<number, boolean>
  >({});

  useEffect(() => {
    if (!shouldLoad) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function loadPurchasers() {
      const cacheKey = purchasersPageCacheKey(page, limit);
      const cached = persistentPurchasersPageCache.get(cacheKey);

      if (cached && !forcePurchasersListRefresh) {
        if (cancelled) return;
        const cachedPurchasers = removeOptimisticCreatedPurchasersFromPage(
          cached.purchasers,
          cached.page,
        );
        setPurchasers(cachedPurchasers);
        setPurchaserBrands(buildPurchaserBrandsMap(cachedPurchasers));
        seedPurchaserConfigTemplateCache(
          cachedPurchasers,
          persistentPurchaserConfigTemplateCache,
        );
        setTotal(cached.total);
        setTotalActiveCount(cached.totalActiveCount ?? 0);
        setPage(cached.page);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await getPurchasersUseCase.execute(page, limit);
        if (cancelled) return;
        const nextItems = removeOptimisticCreatedPurchasersFromPage(
          response.items,
          response.page,
        );
        const nextPage = response.page ?? page;
        setPurchasers(nextItems);
        setPurchaserBrands(buildPurchaserBrandsMap(nextItems));
        seedPurchaserConfigTemplateCache(
          nextItems,
          persistentPurchaserConfigTemplateCache,
        );
        setTotal(response.total);
        setTotalActiveCount(response.totalActiveCount);
        setPage(nextPage);

        persistentPurchasersPageCache.set(cacheKey, {
          purchasers: nextItems,
          total: response.total,
          totalActiveCount: response.totalActiveCount,
          page: nextPage,
          limit,
        });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load purchasers");
        setPurchasers([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setForcePurchasersListRefresh(false);
        }
      }
    }

    void loadPurchasers();
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, page, limit, forcePurchasersListRefresh, getPurchasersUseCase]);

  const refetchPurchasers = useCallback(() => {
    persistentPurchaserConfigTemplateCache.clear();
    setForcePurchasersListRefresh(true);
  }, []);

  const syncPurchasersPageCache = useCallback(
    (
      nextPurchasers: Purchaser[],
      nextTotal: number,
      nextActiveCount: number,
    ) => {
      const cacheKey = purchasersPageCacheKey(page, limit);
      persistentPurchasersPageCache.set(cacheKey, {
        purchasers: nextPurchasers,
        total: nextTotal,
        totalActiveCount: nextActiveCount,
        page,
        limit,
      });
    },
    [page, limit],
  );

  const fetchPurchaserBrands = useCallback(
    async (purchaserId: number) => {
      setPurchaserBrandsLoading((prev) => ({ ...prev, [purchaserId]: true }));
      try {
        const brands = await getPurchaserBrandsUseCase.execute(purchaserId);
        setPurchaserBrands((prev) => ({ ...prev, [purchaserId]: brands }));
        return brands;
      } finally {
        setPurchaserBrandsLoading((prev) => ({ ...prev, [purchaserId]: false }));
      }
    },
    [getPurchaserBrandsUseCase],
  );

  const applyPurchaserCreate = useCallback(
    (created: Purchaser) => {
      const normalized = PurchaserEntity.from({
        ...PurchaserEntity.toJSON(created),
        associatedBrands: created.associatedBrands ?? [],
        associatedBrandsCount: created.associatedBrandsCount ?? 0,
        configTemplate: null,
      });

      getPurchaserIdentityKeys(normalized).forEach((key) =>
        optimisticallyCreatedPurchaserKeys.add(key),
      );

      persistentPurchaserConfigTemplateCache.set(normalized.purchaserId, null);

      setPurchasers((prev) => {
        const alreadyListed = prev.some(
          (item) =>
            item.purchaserId === normalized.purchaserId ||
            item.purchaserKey === normalized.purchaserKey,
        );
        if (alreadyListed) return prev;

        const onFirstPage = page === 1;
        let nextItems = prev;
        if (onFirstPage) {
          nextItems = [normalized, ...prev];
          if (nextItems.length > limit) {
            nextItems = nextItems.slice(0, limit);
          }
        }

        const nextTotal = total + 1;
        const nextActiveCount = normalized.purchaserIsActive
          ? totalActiveCount + 1
          : totalActiveCount;
        setTotal(nextTotal);
        setTotalActiveCount(nextActiveCount);
        syncPurchasersPageCache(nextItems, nextTotal, nextActiveCount);

        persistentPurchasersPageCache.forEach((entry, cacheKey) => {
          const isCurrentPage =
            cacheKey === purchasersPageCacheKey(page, limit);
          if (isCurrentPage) return;

          persistentPurchasersPageCache.set(cacheKey, {
            ...entry,
            purchasers: removeOptimisticCreatedPurchasersFromPage(
              entry.purchasers,
              entry.page,
            ),
            total: nextTotal,
            totalActiveCount: nextActiveCount,
          });
        });

        return nextItems;
      });

      setPurchaserBrands((prev) => ({
        ...prev,
        [normalized.purchaserId]: [],
      }));
    },
    [limit, page, syncPurchasersPageCache, total, totalActiveCount],
  );

  const applyPurchaserUpdate = useCallback(
    (purchaser: Purchaser) => {
      setPurchasers((prev) => {
        const index = prev.findIndex(
          (item) => item.purchaserId === purchaser.purchaserId,
        );
        if (index === -1) return prev;

        const existing = prev[index];
        const wasActive = existing.purchaserIsActive;
        const isActive = purchaser.purchaserIsActive;
        let nextActiveCount = totalActiveCount;

        if (wasActive !== isActive) {
          nextActiveCount = isActive
            ? totalActiveCount + 1
            : Math.max(0, totalActiveCount - 1);
        }

        const next = [...prev];
        next[index] = PurchaserEntity.from({
          ...PurchaserEntity.toJSON(purchaser),
          associatedBrands: existing.associatedBrands,
          associatedBrandsCount: existing.associatedBrandsCount,
          configTemplate: existing.configTemplate,
        });
        const sorted = [...next];
        sorted.sort((a, b) =>
          a.purchaserName.localeCompare(b.purchaserName),
        );

        if (nextActiveCount !== totalActiveCount) {
          setTotalActiveCount(nextActiveCount);
        }
        syncPurchasersPageCache(sorted, total, nextActiveCount);
        return sorted;
      });
    },
    [syncPurchasersPageCache, total, totalActiveCount],
  );

  const applyBrandAssociationUpdate = useCallback(
    (purchaserId: number, association: PurchaserBrandAssociation) => {
      let isNewAssociation = false;

      setPurchaserBrands((prev) => {
        const existing = prev[purchaserId] ?? [];
        const index = existing.findIndex(
          (item) => item.brandId === association.brandId,
        );
        isNewAssociation = index === -1;
        const next =
          index === -1
            ? [association, ...existing]
            : existing.map((item, i) => (i === index ? association : item));
        return { ...prev, [purchaserId]: next };
      });

      if (!isNewAssociation) return;

      setPurchasers((prev) => {
        const nextPurchasers = prev.map((purchaser) => {
          if (purchaser.purchaserId !== purchaserId) return purchaser;
          return PurchaserEntity.from({
            ...PurchaserEntity.toJSON(purchaser),
            associatedBrandsCount: (purchaser.associatedBrandsCount ?? 0) + 1,
            associatedBrands: [
              {
                brandId: association.brandId,
                brandName: association.brandName,
                brandKey: association.brandKey,
                brandPurchaserId: association.brandPurchaserId,
                purchaserInboxEmailId: association.purchaserInboxEmailId,
                isActive: association.isActive,
              },
              ...purchaser.associatedBrands,
            ],
          });
        });
        syncPurchasersPageCache(nextPurchasers, total, totalActiveCount);
        return nextPurchasers;
      });
    },
    [syncPurchasersPageCache, total, totalActiveCount],
  );

  const getPurchaserConfigTemplate = useCallback(
    async (purchaserId: number) => {
      if (persistentPurchaserConfigTemplateCache.has(purchaserId)) {
        return persistentPurchaserConfigTemplateCache.get(purchaserId) ?? null;
      }

      const template =
        await getPurchaserConfigTemplateUseCase.execute(purchaserId);
      persistentPurchaserConfigTemplateCache.set(purchaserId, template);
      return template;
    },
    [getPurchaserConfigTemplateUseCase],
  );

  const updatePurchaserConfigTemplate = useCallback(
    async (params: UpdatePurchaserConfigTemplateDTO) => {
      const template =
        await updatePurchaserConfigTemplateUseCase.execute(params);
      persistentPurchaserConfigTemplateCache.set(
        params.data.purchaserId,
        template,
      );
      invalidatePurchaserConfigTemplate(params.data.purchaserId);
      return template;
    },
    [updatePurchaserConfigTemplateUseCase],
  );

  const getCachedPurchaserPortalKeys = useCallback(
    (purchaserId: number) =>
      persistentPurchaserPortalKeyCache.has(purchaserId)
        ? persistentPurchaserPortalKeyCache.get(purchaserId) ?? []
        : undefined,
    [],
  );

  const getPurchaserPortalKeys = useCallback(
    async (purchaserId: number) => {
      const cached = getCachedPurchaserPortalKeys(purchaserId);
      if (cached !== undefined) {
        return cached;
      }

      const keys = await getPurchaserPortalKeysUseCase.execute(purchaserId);
      persistentPurchaserPortalKeyCache.set(purchaserId, keys);
      return keys;
    },
    [getCachedPurchaserPortalKeys, getPurchaserPortalKeysUseCase],
  );

  const createPurchaserPortalKey = useCallback(
    async (params: CreatePurchaserPortalKeyDTO) => {
      const created = await createPurchaserPortalKeyUseCase.execute(params);
      const current =
        persistentPurchaserPortalKeyCache.get(params.data.purchaserId) ?? [];
      persistentPurchaserPortalKeyCache.set(params.data.purchaserId, [
        ...current,
        created,
      ]);
      return created;
    },
    [createPurchaserPortalKeyUseCase],
  );

  const updatePurchaserPortalKey = useCallback(
    async (params: UpdatePurchaserPortalKeyDTO) => {
      const updated = await updatePurchaserPortalKeyUseCase.execute(params);
      const current = persistentPurchaserPortalKeyCache.get(
        params.data.purchaserId,
      );
      if (current) {
        persistentPurchaserPortalKeyCache.set(
          params.data.purchaserId,
          current.map((item) => (item.id === updated.id ? updated : item)),
        );
      }
      return updated;
    },
    [updatePurchaserPortalKeyUseCase],
  );

  const createPurchaser = useCallback(
    (params: CreatePurchaserDTO) => createPurchaserUseCase.execute(params),
    [createPurchaserUseCase],
  );

  const updatePurchaser = useCallback(
    (params: UpdatePurchaserDTO) => updatePurchaserUseCase.execute(params),
    [updatePurchaserUseCase],
  );

  const createBrandPurchaserAssociation = useCallback(
    (params: CreateBrandPurchaserAssociationDTO) =>
      createBrandPurchaserAssociationUseCase.execute(params),
    [createBrandPurchaserAssociationUseCase],
  );

  const value = useMemo(
    (): PurchasersDataContextValue => ({
      purchasers,
      total,
      totalActiveCount,
      page,
      limit,
      loading,
      error,
      purchaserBrands,
      purchaserBrandsLoading,
      setPage,
      refetchPurchasers,
      fetchPurchaserBrands,
      getPurchaserConfigTemplate,
      updatePurchaserConfigTemplate,
      getPurchaserPortalKeys,
      getCachedPurchaserPortalKeys,
      createPurchaserPortalKey,
      updatePurchaserPortalKey,
      createPurchaser,
      updatePurchaser,
      createBrandPurchaserAssociation,
      applyPurchaserCreate,
      applyPurchaserUpdate,
      applyBrandAssociationUpdate,
    }),
    [
      purchasers,
      total,
      totalActiveCount,
      page,
      limit,
      loading,
      error,
      purchaserBrands,
      purchaserBrandsLoading,
      refetchPurchasers,
      fetchPurchaserBrands,
      getPurchaserConfigTemplate,
      updatePurchaserConfigTemplate,
      getPurchaserPortalKeys,
      getCachedPurchaserPortalKeys,
      createPurchaserPortalKey,
      updatePurchaserPortalKey,
      createPurchaser,
      updatePurchaser,
      createBrandPurchaserAssociation,
      applyPurchaserCreate,
      applyPurchaserUpdate,
      applyBrandAssociationUpdate,
    ],
  );

  return (
    <PurchasersDataContext.Provider value={value}>
      {children}
    </PurchasersDataContext.Provider>
  );
}
